import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { adminApi, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useDragReorder, move } from "@/admin/dnd";
import { ConfirmDialog } from "@/admin/ConfirmDialog";

const GroupCard = ({ group, onReorder, onDelete }) => {
  const schools = group.schools;
  const drag = useDragReorder(schools, (next) => onReorder(group, next));
  return (
    <div className="rounded-xl border bg-white p-4" data-testid={`group-card-${group.id}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold uppercase tracking-wide text-brand text-sm">{group.name} <span className="text-muted-foreground font-normal normal-case">({schools.length})</span></h3>
        <button type="button" onClick={() => onDelete(group)} className="p-1.5 text-muted-foreground hover:text-destructive" aria-label="Remove group" data-testid={`group-delete-${group.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <ul className="space-y-1.5">
        {schools.map((s, i) => (
          <li key={s.id} {...drag(i)} className="flex items-center gap-2 rounded-lg border bg-ivory px-2 py-1.5 text-sm cursor-move" data-testid={`group-school-${s.slug}`}>
            <GripVertical className="h-4 w-4 text-charcoal/30 shrink-0" />
            <span className="flex-1 truncate">{s.name}</span>
            <button type="button" onClick={() => onReorder(group, move(schools, i, i - 1))} disabled={i === 0} className="p-1 disabled:opacity-30 hover:text-brand" aria-label="Move up" data-testid={`group-school-up-${s.slug}`}><ChevronUp className="h-4 w-4" /></button>
            <button type="button" onClick={() => onReorder(group, move(schools, i, i + 1))} disabled={i === schools.length - 1} className="p-1 disabled:opacity-30 hover:text-brand" aria-label="Move down" data-testid={`group-school-down-${s.slug}`}><ChevronDown className="h-4 w-4" /></button>
          </li>
        ))}
        {!schools.length && <li className="text-xs text-muted-foreground">No schools yet</li>}
      </ul>
    </div>
  );
};

export default function Groups() {
  const { groups, refresh } = useSite();
  const [list, setList] = useState(groups);
  const [name, setName] = useState("");
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { setList(groups); }, [groups]);

  const add = async () => {
    if (!name.trim()) return;
    try {
      await adminApi.post("/admin/groups", { name });
      setName("");
      toast.success("Group added");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reorder = async (group, schools) => {
    setList((l) => l.map((g) => (g.id === group.id ? { ...g, schools } : g)));
    try {
      await adminApi.put(`/admin/groups/${group.id}/order`, { school_ids: schools.map((s) => s.id) });
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try {
      await adminApi.delete(`/admin/groups/${deleting.id}`);
      toast.success("Group removed");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
    setDeleting(null);
  };

  return (
    <div data-testid="admin-groups-page">
      <h1 className="text-2xl font-bold text-charcoal">Groups</h1>
      <p className="text-sm text-muted-foreground mb-6">Groups appear A–Z in the Search Schools menu. Drag or use the arrows to order schools within a group.</p>
      <div className="flex gap-2 max-w-md mb-8">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New group name" className="bg-white" onKeyDown={(e) => e.key === "Enter" && add()} data-testid="group-name-input" />
        <Button onClick={add} className="bg-brand hover:bg-brand-dark" data-testid="group-add-button"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {list.map((g) => <GroupCard key={g.id} group={g} onReorder={reorder} onDelete={setDeleting} />)}
      </div>
      <ConfirmDialog open={!!deleting} title="Remove group?" description={deleting?.schools?.length ? `"${deleting?.name}" still has listings — removal will be blocked until they are moved or deleted.` : `"${deleting?.name}" will be removed.`} onCancel={() => setDeleting(null)} onConfirm={remove} />
    </div>
  );
}
